import React from "react"
import { css } from "styled-components"
import Text, { IRichTextProps, RichText } from "../atoms/text"
import { T } from "../styles/typography"
import VariantContainer, { IStyleVariant } from "../atoms/variant-container"
import mergeVariants from "../util/merge-variants"
import { BT } from "../styles/blog-typography"
import { BlogRichTextOptions } from "../atoms/rich-text/blog-rich-text"

interface IContentBlock {
  title: string | JSX.Element
  titleTag?: string
  titleVariants?: IStyleVariant | any[]
  bodyVariants?: IStyleVariant | any[]
  darkMode?: boolean
  description: string | JSX.Element
  variants?: IStyleVariant | any[]
}

function ContentBlock({
  title,
  titleTag,
  titleVariants,
  bodyVariants,
  darkMode = false,
  description,
  variants,
  ...rest
}: IContentBlock) {
  return (
    <VariantContainer variants={mergeVariants([], variants)} {...rest}>
      <Text
        as={titleTag}
        variants={mergeVariants(
          {
            sm: css`
              color: ${darkMode ? "var(--light-background)" : "var(--slate)"};
              ${titleVariants?.sm};
            `,
          },
          titleVariants
        )}
      >
        {title}
      </Text>
      <Text.Body
        variants={mergeVariants(
          {
            sm: css`
              color: ${darkMode
                ? "var(--light-background)"
                : "var(--body-text-grey)"};
            `,
          },
          bodyVariants
        )}
      >
        {description}
      </Text.Body>
    </VariantContainer>
  )
}

ContentBlock.H2 = ({
  title,
  description,
  titleVariants,
  darkMode,
  ...rest
}: IContentBlock) => (
  <ContentBlock
    title={title}
    titleTag="h2"
    titleVariants={mergeVariants(
      {
        sm: css`
          ${T.sm.h2(500)};
          margin-bottom: 24px;
        `,
        md: T.md.h2(500),
        lg: T.lg.h2(500),
      },
      titleVariants
    )}
    description={description}
    {...rest}
  />
)

ContentBlock.H3 = ({
  title,
  description,
  titleVariants,
  darkMode,
  ...rest
}: IContentBlock) => (
  <ContentBlock
    title={title}
    titleTag="h3"
    titleVariants={mergeVariants(
      {
        sm: css`
          ${T.sm.h3`500`};
          margin-bottom: 24px;
        `,
        md: css`
          ${T.md.h3`500`};
          margin-bottom: 16px;
        `,
        lg: css`
          ${T.lg.h3`500`};
          margin-bottom: 24px;
        `,
      },
      titleVariants
    )}
    description={description}
    {...rest}
  />
)

ContentBlock.ResourceCard = ({
  title,
  description,
  titleVariants,
  bodyVariants,
  darkMode,
  ...rest
}: IContentBlock) => (
  <ContentBlock
    title={title}
    titleVariants={mergeVariants(
      {
        sm: css`
          ${T.sm.h4`500`};
          margin-bottom: 16px;
          text-align: center;
          text-transform: unset;
          letter-spacing: unset;
          color: var(--slate);
          line-height: 140%;
        `,
        md: css`
          ${T.md.h4`500`};
          text-transform: unset;
          letter-spacing: unset;
          margin-bottom: 12px;
          line-height: 140%;
        `,
        lg: css`
          ${T.lg.h5`500`};
          text-transform: unset;
          letter-spacing: unset;
          line-height: 140%;
        `,
      },
      titleVariants
    )}
    bodyVariants={mergeVariants(
      {
        sm: css`
          ${T.body.small`400`};
          margin-bottom: 0px;
          text-align: center;
        `,
        md: css`
          ${T.body.small`400`};
          margin-bottom: 0px;
        `,
        lg: css`
          ${T.body.small`400`};
          margin-bottom: 0px;
        `,
      },
      bodyVariants
    )}
    description={description}
    {...rest}
  />
)

ContentBlock.RichText = ({
  children,
  options,
  variants,
}: IRichTextProps & { variants?: IStyleVariant | any[] }) => {
  return (
    <VariantContainer
      variants={mergeVariants(
        [
          css`
            grid-column: 1/-1;
            overflow-wrap: break-word;
            word-wrap: break-word;
          `,
        ],
        variants
      )}
    >
      <RichText options={options}>{children}</RichText>
    </VariantContainer>
  )
}

ContentBlock.BlogRichText = ({ children }) => (
  <ContentBlock.RichText
    variants={[
      css`
        ${BT.sm.body`400`}
        margin: 32px 0;
      `,
      css`
        margin: 32px 0 64px 0;
      `,
      css`
        margin: 32px 0 48px 0;
      `,
    ]}
    options={BlogRichTextOptions}
  >
    {children}
  </ContentBlock.RichText>
)

export default ContentBlock
